<template>
  <v-card id="add-coupons">
    <v-card-title class="i-card-title-div">
      <v-row class="align-center">
        <v-col cols="12" sm="9">
          <span class="g-bold g-heading-l">{{
            newCoupon ? 'New Coupon' : 'Edit Coupon'
          }}</span>
        </v-col>
        <v-col cols="12" sm="3">
          <i-instructions
            instructionKey="data-maintenance/coupons/add-dialog"
          ></i-instructions>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-8 i-card-content-div">
      <v-alert
        border="left"
        class="mt-1 mb-5 i-alert"
        dense
        text
        outlined
        type="info"
        elevation="1"
        v-if="!newCoupon"
      >
        <template v-if="activeCoupon"
          >For active Coupons only End Date can be modified</template
        >
        <template v-else
          >For upcoming Coupons Target Type cannot be modified</template
        >
      </v-alert>

      <v-form v-model="form" @submit.prevent>
        <span class="g-bold g-heading-s g-text-color-black">Target Type</span>
        <div class="mb-6">
          <v-radio-group
            class="i-radio-group"
            :disabled="!newCoupon"
            @change="resetTargetTypeForm"
            v-model="targetType"
          >
            <v-row>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetType === ALL_GEOGRAPHY }"
                >
                  <v-radio
                    label="All (Geography)"
                    :value="ALL_GEOGRAPHY"
                  ></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetType === RETAILER_STORE }"
                >
                  <v-radio
                    :label="isILR ? 'Customer(s)' : 'Retailer(s)'"
                    :value="RETAILER_STORE"
                  ></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetType === BANNER_GROUP }"
                >
                  <v-radio
                    label="Banner Group(s)"
                    :value="BANNER_GROUP"
                  ></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{
                    'item-div-active': targetType === MULTI_STORE_OPERATOR
                  }"
                >
                  <v-radio
                    label="Multi-Store Operator(s)"
                    :value="MULTI_STORE_OPERATOR"
                  ></v-radio>
                </div>
              </v-col>
            </v-row>
          </v-radio-group>
        </div>

        <template v-if="targetType === RETAILER_STORE">
          <p class="g-bold g-heading-s g-text-color-black">
            {{ isILR ? 'Customer(s)' : 'Retailer(s)' }}
          </p>
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :disabled="activeCoupon"
            :items="availableStores"
            :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
            :item-disabled="savedListApplied ? () => true : () => false"
            multiple
            outlined
            :rules="[
              (v) =>
                !!v.length ||
                `${isILR ? 'Customer(s)' : 'Retailer(s)'} required`
            ]"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetStoreIds"
            @change="handleStoreSelected"
            @click:clear="resetRetailers"
          >
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item v-bind="attrs" v-on="on" #default="{ active }">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="active"
                    :model-value="active"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-row no-gutters align="center">
                    <span>{{ item.text }}</span>
                    <v-spacer></v-spacer>
                    <v-chip
                      class="mx-1"
                      :color="item.disabled ? 'grey' : 'orange'"
                      small
                      style="font-size: 0.9em !important"
                      text-color="white"
                      ><strong
                        >Min. Order:
                        {{ $filters.currency(item.minimumOrderValue) }} | Free
                        Freight:
                        {{
                          $filters.currency(
                            item.minimumOrderValueFreeFreightNotApplicable
                              ? undefined
                              : item.minimumOrderValueFreeFreight,
                            'N/A'
                          )
                        }}</strong
                      >
                    </v-chip>
                  </v-row>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
          <retailer-list-action-button
            class="mb-2"
            :supplier-id="selectedSupplierId"
            :selected-retailers="targetStoreIds"
            :disabled="activeCoupon"
            mode="useOnly"
            @apply="handleApplySavedRetailers"
            :saved-list-applied="savedListApplied"
          />
        </template>

        <template v-if="targetType === BANNER_GROUP">
          <span class="g-bold g-heading-s g-text-color-black mt-3 mt-md-5"
            >Banner Group(s)</span
          >
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :disabled="activeCoupon"
            :items="targetBannerGroups"
            label="Select Banner Group(s)"
            multiple
            outlined
            :rules="[(v) => !!v.length || 'Banner Group(s) required']"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetBannerGroupIds"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>

        <template v-if="targetType === MULTI_STORE_OPERATOR">
          <span class="g-bold g-heading-s g-text-color-black mt-3 mt-md-5"
            >Multi-Store Operator(s)</span
          >
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :disabled="activeCoupon"
            :items="targetMultiStoreOperators"
            label="Select Multi-Store Operator(s)"
            multiple
            outlined
            :rules="[(v) => !!v.length || 'Multi-Store Operator(s) required']"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetMultiStoreOperatorIds"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>

        <template v-if="targetType !== RETAILER_STORE">
          <span class="g-bold g-heading-s g-text-color-black mt-2"
            >Coupon Geography</span
          >
          <div class="mb-6">
            <v-radio-group
              class="i-radio-group"
              :disabled="activeCoupon"
              @change="resetTargetGeographyForm"
              v-model="targetGeography"
            >
              <v-row>
                <v-col cols="12" md="4" class="pb-0">
                  <div
                    class="item-div-mini mb-0"
                    :class="{ 'item-div-active': targetGeography === NATIONAL }"
                  >
                    <v-radio
                      label="National"
                      :value="NATIONAL"
                      :disabled="!targetNationalApplicable"
                    ></v-radio>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pb-0">
                  <div
                    class="item-div-mini mb-0"
                    :class="{ 'item-div-active': targetGeography === STATE }"
                  >
                    <v-radio label="State(s)" :value="STATE"></v-radio>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pb-0">
                  <div
                    class="item-div-mini mb-0"
                    :class="{ 'item-div-active': targetGeography === ZONE }"
                  >
                    <v-radio label="Zone(s)" :value="ZONE"></v-radio>
                  </div>
                </v-col>
              </v-row>
            </v-radio-group>
          </div>

          <template v-if="targetGeography === STATE">
            <span class="g-bold g-heading-s g-text-color-black mt-2"
              >State(s)</span
            >
            <div class="mb-2">
              <v-btn
                text
                small
                color="secondary"
                :disabled="activeCoupon"
                @click="handleAddAllStates"
              >
                Add options of all states
              </v-btn>
            </div>
            <v-autocomplete
              attach
              background-color="#0000000f"
              class="mt-3 mb-6"
              clearable
              deletable-chips
              dense
              :disabled="activeCoupon"
              :items="availableStates"
              label="Select State(s)"
              multiple
              outlined
              :rules="[
                (v) => !!v.length || 'State(s) required for State coupons'
              ]"
              v-model:search-input="searchInput"
              small-chips
              v-model="targetStoreDeliveryAddressStates"
              @change="handleStateSelected"
            >
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on" #default="{ active }">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="active"
                      :model-value="active"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-row no-gutters align="center">
                      <span>{{ item.text }}</span>
                      <v-spacer></v-spacer>
                      <v-chip
                        class="mx-1"
                        :color="item.disabled ? 'grey' : 'orange'"
                        small
                        style="font-size: 0.9em !important"
                        text-color="white"
                        ><strong
                          >Min. Order:
                          {{ $filters.currency(item.minimumOrderValue) }} | Free
                          Freight:
                          {{
                            $filters.currency(
                              item.minimumOrderValueFreeFreightNotApplicable
                                ? undefined
                                : item.minimumOrderValueFreeFreight,
                              'N/A'
                            )
                          }}</strong
                        >
                      </v-chip>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </template>

          <template v-if="targetGeography === ZONE">
            <span class="g-bold g-heading-s g-text-color-black mt-2"
              >Zone(s)</span
            >
            <v-autocomplete
              attach
              background-color="#0000000f"
              class="mt-3 mb-6"
              clearable
              deletable-chips
              dense
              :disabled="activeCoupon"
              :items="availableZones"
              label="Select Zone(s)"
              multiple
              outlined
              :rules="[
                (v) => !!v.length || 'Zone(s) required for Zone coupons'
              ]"
              v-model:search-input="searchInput"
              small-chips
              v-model="targetStoreDeliveryAddressZones"
              @change="handleZoneSelected"
            >
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on" #default="{ active }">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="active"
                      :model-value="active"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-row no-gutters align="center">
                      <span>{{ item.text }}</span>
                      <v-spacer></v-spacer>
                      <v-chip
                        class="mx-1"
                        :color="item.disabled ? 'grey' : 'orange'"
                        small
                        style="font-size: 0.9em !important"
                        text-color="white"
                        ><strong
                          >Min. Order:
                          {{ $filters.currency(item.minimumOrderValue) }} | Free
                          Freight:
                          {{
                            $filters.currency(
                              item.minimumOrderValueFreeFreightNotApplicable
                                ? undefined
                                : item.minimumOrderValueFreeFreight,
                              'N/A'
                            )
                          }}</strong
                        >
                      </v-chip>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </template>
        </template>

        <span class="g-bold g-heading-s g-text-color-black mt-2">
          Coupon Date Range
          <v-icon
            class="i-tooltip-color pb-1"
            small
            v-tooltip="
              'Starts at 12:00am & Ends at 11:59pm on the submitted dates'
            "
          >
            mdi-help-circle-outline
          </v-icon>
        </span>

        <v-row>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="15"
              :nudge-top="30"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  class="mt-0"
                  :disabled="activeCoupon"
                  v-model="startDateDisplay"
                  label="Start Date *"
                  background-color="#0000000f"
                  dense
                  outlined
                  append-icon="mdi-calendar"
                  readonly
                  v-on="on"
                  :rules="[
                    (v) => !!v || 'Start Date required',
                    validStartSate(startDate, endDate)
                  ]"
                ></v-text-field>
              </template>
              <v-date-picker
                :min="minAllowedStartDate"
                v-model="startDate"
                @change="startDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="15"
              :nudge-top="30"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-model="endDateDisplay"
                  label="End Date *"
                  outlined
                  append-icon="mdi-calendar"
                  background-color="#0000000f"
                  dense
                  readonly
                  v-on="on"
                  :rules="[
                    (v) => !!v || 'End Date required',
                    validEndDate(startDate, endDate)
                  ]"
                ></v-text-field>
              </template>
              <v-date-picker
                :min="minAllowedEndDate"
                v-model="endDate"
                @change="endDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-row align="center">
          <v-col cols="12" sm="6" class="pb-0">
            <span class="g-bold g-heading-s g-text-color-black">
              Minimum Coupon value
              <v-icon
                class="i-tooltip-color pb-1"
                small
                v-tooltip="{
                  content: `As defined by ${
                    supplierTradingRules.minimumOrderValueFreeFreightNotApplicable ===
                    false
                      ? 'Free Freight'
                      : 'Minimum Order'
                  } value in Supplier Trading Rules`
                }"
              >
                mdi-help-circle-outline
              </v-icon>
            </span>
          </v-col>
          <v-col cols="12" sm="6" class="pb-0">
            <div
              class="g-heading-s g-bold g-border-radius-s i-status-block-green c-border py-2 px-6 text-center"
            >
              {{ $filters.currency(minimumCouponValue) }}
            </div>
          </v-col>
        </v-row>

        <v-alert
          border="left"
          dense
          outlined
          text
          type="info"
          elevation="1"
          class="mt-8 i-alert"
        >
          <span
            >Coupon Spend & Save minus Coupon Discount must be greater or equal
            to current Minimum Coupon value</span
          >
        </v-alert>

        <v-row class="mt-8">
          <v-col cols="12" sm="6">
            <span class="g-bold g-heading-s g-text-color-black">
              Coupon Spend & Save
              <v-icon
                class="i-tooltip-color pb-1"
                small
                v-tooltip="
                  'Minimum order spend required to activate Coupon Discount'
                "
              >
                mdi-help-circle-outline
              </v-icon>
            </span>
            <div class="mt-3">
              <i-numeric-field
                background-color="#0000000f"
                dense
                :disabled="activeCoupon"
                label="Coupon Spend & Save *"
                :min="0"
                :max="9999.99"
                outlined
                :precision="2"
                :prefix="currencySymbol"
                required
                :rules="[
                  (v) => !!v || 'Coupon Spend & Save required',
                  spendNotUnderMinimumCouponValue(
                    minimumOrderValue,
                    minimumCouponValue
                  )
                ]"
                v-model="minimumOrderValue"
              >
              </i-numeric-field>
            </div>
          </v-col>

          <v-col cols="12" sm="6">
            <span class="g-bold g-heading-s g-text-color-black">
              Coupon Discount
              <v-icon
                class="i-tooltip-color pb-1"
                small
                v-tooltip="
                  'Applies if retailer order achieves Coupon Spend & Save'
                "
                >mdi-help-circle-outline
              </v-icon>
            </span>
            <div class="mt-3">
              <i-numeric-field
                background-color="#0000000f"
                class="mt-3"
                dense
                :disabled="activeCoupon"
                label="Coupon Discount *"
                :max="9999.99"
                :min="0"
                outlined
                :prefix="currencySymbol"
                :precision="2"
                required
                :rules="[
                  (v) => !!v || 'Coupon Discount required',
                  notLowerThanMinimumCouponValue(
                    minimumOrderValue,
                    amountOffExGst,
                    minimumCouponValue
                  )
                ]"
                v-model="amountOffExGst"
              >
              </i-numeric-field>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions class="i-dialog-actions-div pt-6 justify-end">
      <v-btn rounded text class="i-btn-text-black" @click="$emit('cancel')"
        >Cancel</v-btn
      >
      <v-btn
        rounded
        class="i-btn-block-secondary mr-4"
        :disabled="!validCoupon"
        :loading="saving"
        @click="saveCoupon"
        >{{ newCoupon ? 'Create' : 'Update' }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState, mapGetters } from 'vuex'
import { addCoupon, updateCoupon } from '@/services/coupons'
import FieldValidators from '@/utils/FieldValidators'
import {
  minAllowedPromotionEndDate,
  minAllowedPromotionStartDate
} from '@/modules/dataMaintenance/helper/PromotionPublishSchedule'
import iNumericField from '@/components/NumericField.vue'
import iInstructions from '@/components/Instructions'
import RetailerListActionButton from '@/components/RetailerListActionButton.vue'

const FIELD_RULES = {
  spendNotUnderMinimumCouponValue: (minimumOrderValue, minimumCouponValue) => {
    return (
      Number(minimumOrderValue) >= minimumCouponValue ||
      'Must be equal or higher than Minimum Coupon value'
    )
  },

  notLowerThanMinimumCouponValue: (
    minimumOrderValue = 0,
    amountOffExGst,
    minimumCouponValue
  ) => {
    return (
      Number(minimumOrderValue - amountOffExGst) >= minimumCouponValue ||
      'Coupon Spend minus Coupon Discount must be equal or higher than Minimum Coupon value'
    )
  }
}

const COUPON_FIELDS = {
  couponId: null,
  targetType: null,
  targetGeography: null,
  targetBannerGroupIds: [],
  targetMultiStoreOperatorIds: [],
  targetStoreIds: [],
  targetStoreDeliveryAddressStates: [],
  targetStoreDeliveryAddressZones: [],
  startDate: undefined,
  endDate: undefined,
  minimumOrderValue: null,
  amountOffExGst: null
}

const COUPON_GEOGRAPHY = {
  NATIONAL: 'NATIONAL',
  STATE: 'STATE',
  ZONE: 'ZONE'
}

const TARGET_TYPE = {
  ALL_GEOGRAPHY: 'ALL_GEOGRAPHY',
  BANNER_GROUP: 'BANNER_GROUP',
  MULTI_STORE_OPERATOR: 'MULTI_STORE_OPERATOR',
  RETAILER_STORE: 'RETAILER_STORE'
}

export default {
  components: {
    iNumericField,
    iInstructions,
    RetailerListActionButton
  },
  props: {
    coupon: Object
  },

  data: () => ({
    form: false,
    saving: false,
    endDateMenu: false,
    startDateMenu: false,
    searchInput: '',
    originalCoupon: {},
    targetMultiStoreOperators: [],
    targetBannerGroups: [],
    tradingRulesByPricingRegion: [],
    savedListApplied: false,
    stores: [],
    ...FieldValidators,
    ...COUPON_FIELDS,
    ...COUPON_GEOGRAPHY,
    ...FIELD_RULES,
    ...TARGET_TYPE
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('stores', ['retailerStores']),
    ...mapState('pricingRegions', ['pricingRegions']),

    ...mapState('freightInfo', { supplierTradingRules: 'info' }),
    ...mapGetters('tenant', [
      'isILR',
      'tenantCurrency',
      'tenantLocale',
      'tenantCountryRegion'
    ]),

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    minimumCouponValue() {
      const tradingRule = this.applicableTradingRule()
      return tradingRule?.minimumOrderValueFreeFreightNotApplicable === false
        ? tradingRule?.minimumOrderValueFreeFreight
        : tradingRule?.minimumOrderValue
    },

    targetNationalApplicable() {
      const referenceZonePrice = this.supplierTradingRules.zonePrices[0]
      return this.supplierTradingRules.zonePrices.every((zonePrice) => {
        return (
          zonePrice.minimumOrderValue ===
            referenceZonePrice.minimumOrderValue &&
          zonePrice.minimumOrderValueFreeFreight ===
            referenceZonePrice.minimumOrderValueFreeFreight &&
          zonePrice.minimumOrderValueFreeFreightNotApplicable ===
            referenceZonePrice.minimumOrderValueFreeFreightNotApplicable
        )
      })
    },

    states() {
      return [...new Set(this.pricingRegions.map((region) => region.state))]
    },

    availableStores() {
      const referenceStore = this.retailerStores.find(
        (s) => s.id === this.targetStoreIds[0]
      )
      const referenceTradingRule = this.findTradingRuleForZone(
        referenceStore?.pricingRegionExternalCode
      )
      return this.stores.map((store) => {
        const storePricingRegion = store.pricingRegionExternalCode
        const storeZoneTradingRule =
          this.tradingRulesByPricingRegion[storePricingRegion]
        const storeZoneTradingRuleMatchesReferenceRule =
          this.zoneTradingRulesMatch(storeZoneTradingRule, referenceTradingRule)
        return {
          text: store.displayName,
          value: store.id,
          minimumOrderValue: storeZoneTradingRule.minimumOrderValue,
          minimumOrderValueFreeFreight:
            storeZoneTradingRule.minimumOrderValueFreeFreight,
          minimumOrderValueFreeFreightNotApplicable:
            storeZoneTradingRule.minimumOrderValueFreeFreightNotApplicable,
          disabled:
            referenceStore !== undefined &&
            !storeZoneTradingRuleMatchesReferenceRule
        }
      })
    },

    availableStates() {
      const referenceTradingRule = this.findTradingRuleForState(
        this.targetStoreDeliveryAddressStates[0]
      )
      return this.states.map((s) => {
        const stateTradingRule = this.findTradingRuleForState(s)
        const stateTradingRuleMatchesReferenceRule = this.zoneTradingRulesMatch(
          stateTradingRule,
          referenceTradingRule
        )
        return {
          text: s,
          value: s,
          minimumOrderValue: stateTradingRule.minimumOrderValue,
          minimumOrderValueFreeFreight:
            stateTradingRule.minimumOrderValueFreeFreight,
          minimumOrderValueFreeFreightNotApplicable:
            stateTradingRule.minimumOrderValueFreeFreightNotApplicable,
          disabled:
            this.targetStoreDeliveryAddressStates.length &&
            !this.targetStoreDeliveryAddressStates.find((e) => e === s) &&
            !stateTradingRuleMatchesReferenceRule
        }
      })
    },

    availableZones() {
      const referenceTradingRule = this.findTradingRuleForZone(
        this.targetStoreDeliveryAddressZones[0]
      )
      return this.pricingRegions.map((region) => {
        const zoneTradingRule = this.findTradingRuleForZone(region.zoneCode)
        const zoneTradingRuleMatchesReferenceRule = this.zoneTradingRulesMatch(
          zoneTradingRule,
          referenceTradingRule
        )
        return {
          text: region.displayName,
          value: region.zoneCode,
          minimumOrderValue: zoneTradingRule.minimumOrderValue,
          minimumOrderValueFreeFreight:
            zoneTradingRule.minimumOrderValueFreeFreight,
          minimumOrderValueFreeFreightNotApplicable:
            zoneTradingRule.minimumOrderValueFreeFreightNotApplicable,
          disabled:
            this.targetStoreDeliveryAddressZones.length &&
            !this.targetStoreDeliveryAddressZones.find(
              (e) => e === region.zoneCode
            ) &&
            !zoneTradingRuleMatchesReferenceRule
        }
      })
    },

    newCoupon() {
      return _.isEmpty(this.coupon)
    },

    activeCoupon() {
      return this.coupon.status === 'ACTIVE'
    },

    changesMade() {
      return !_.isEqual(this.originalCoupon, this.createBlueprint())
    },

    beforeEndDate() {
      return this.startDate <= this.endDate
    },

    endDateDisplay() {
      return this.$options.filters.formatDatePicker(this.endDate)
    },

    startDateDisplay() {
      return this.$options.filters.formatDatePicker(this.startDate)
    },

    minAllowedEndDate() {
      return minAllowedPromotionEndDate(
        this.startDate,
        this.tenantCountryRegion
      )
    },

    minAllowedStartDate() {
      return minAllowedPromotionStartDate(this.tenantCountryRegion)
    },

    validCouponDates() {
      return this.startDate && this.endDate >= this.startDate
    },

    validGeography() {
      return (
        this.targetType === this.RETAILER_STORE ||
        this.targetGeography === this.NATIONAL ||
        (this.targetGeography === this.STATE &&
          !!this.targetStoreDeliveryAddressStates.length) ||
        (this.targetGeography === this.ZONE &&
          !!this.targetStoreDeliveryAddressZones.length)
      )
    },

    validTargetType() {
      return (
        this.targetType === this.ALL_GEOGRAPHY ||
        (this.targetType === this.RETAILER_STORE &&
          !!this.targetStoreIds.length) ||
        (this.targetType === this.BANNER_GROUP &&
          !!this.targetBannerGroupIds.length) ||
        (this.targetType === this.MULTI_STORE_OPERATOR &&
          !!this.targetMultiStoreOperatorIds.length)
      )
    },

    validCoupon() {
      return (
        this.validTargetType &&
        this.validGeography &&
        this.validCouponDates &&
        this.form &&
        this.changesMade
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    mapPricingRegionToTradingRule() {
      this.pricingRegions.forEach((region) => {
        this.tradingRulesByPricingRegion[region.zoneCode] =
          this.supplierTradingRules.zonePrices.find(
            (p) => p.zoneCode === region.zoneCode
          )
      })
    },

    handleStoreSelected() {
      this.searchInput = ''
    },

    handleStateSelected() {
      this.searchInput = ''
    },

    handleAddAllStates() {
      this.targetStoreDeliveryAddressStates = this.availableStates.map(
        (state) => state.value
      )
      this.searchInput = ''
    },

    handleZoneSelected() {
      this.searchInput = ''
    },

    findTradingRuleForState(state) {
      if (!state) return undefined
      return this.supplierTradingRules.zonePrices.find(
        (zonePrice) => zonePrice.zoneCode.indexOf(state) >= 0
      )
    },

    findTradingRuleForZone(zoneCode) {
      if (!zoneCode) return undefined
      return this.supplierTradingRules.zonePrices.find(
        (zonePrice) => zonePrice.zoneCode === zoneCode
      )
    },

    zoneTradingRulesMatch(tradingRule, otherTradingRule) {
      return (
        tradingRule?.minimumOrderValue ===
          otherTradingRule?.minimumOrderValue &&
        tradingRule?.minimumOrderValueFreeFreight ===
          otherTradingRule?.minimumOrderValueFreeFreight &&
        tradingRule?.minimumOrderValueFreeFreightNotApplicable ===
          otherTradingRule?.minimumOrderValueFreeFreightNotApplicable
      )
    },

    applicableTradingRule() {
      let tradingRule
      if (this.targetType === this.RETAILER_STORE) {
        const referenceStore = this.retailerStores.find(
          (s) => s.id === this.targetStoreIds[0]
        )
        tradingRule = this.findTradingRuleForZone(
          referenceStore?.pricingRegionExternalCode
        )
      } else if (this.targetGeography === this.NATIONAL) {
        tradingRule = this.supplierTradingRules.zonePrices[0]
      } else if (this.targetGeography === this.STATE) {
        tradingRule = this.findTradingRuleForState(
          this.targetStoreDeliveryAddressStates[0]
        )
      } else if (this.targetGeography === this.ZONE) {
        tradingRule = this.findTradingRuleForZone(
          this.targetStoreDeliveryAddressZones[0]
        )
      }
      return tradingRule
    },

    // ***
    // Functions related to help determining if Special has been modified
    // ***
    createBlueprint() {
      return {
        couponId: this.couponId,
        startDate: this.startDate,
        endDate: this.endDate,
        minimumOrderValue: this.minimumOrderValue,
        amountOffExGst: this.amountOffExGst,
        targetGeography: this.targetGeography,
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        targetStoreDeliveryAddressStates: this.targetStoreDeliveryAddressStates,
        targetStoreDeliveryAddressZones: this.targetStoreDeliveryAddressZones,
        targetType: this.targetType
      }
    },

    // ***
    // Functions related to Coupon actions
    // ***

    createCoupon(payload) {
      addCoupon(this.selectedSupplierId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Coupon has been created!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          let msg = ''
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'COUPON_OVERLAP':
              msg = 'There is an existing Coupon for the selected dates!'
              break

            default:
              msg = 'Your coupon could not be created, please try again...'
          }
          this.addNotification({ color: 'error', text: msg })
        })
    },

    copyCoupon() {
      this.couponId = this.coupon.id
      this.startDate = this.coupon.startDate
      this.endDate = this.coupon.endDate
      this.minimumOrderValue =
        this.coupon.minimumOrderValue.amountExGst.toString()
      this.amountOffExGst = this.coupon.amountOff.amountExGst.toString()
      this.targetGeography = this.coupon.targetGeography
      this.targetBannerGroupIds =
        this.coupon.targetBannerGroups?.map(
          (bannerGroup) => bannerGroup.bannerGroupId
        ) || []
      this.targetMultiStoreOperatorIds =
        this.coupon.targetMultiStoreOperators?.map(
          (mso) => mso.multiStoreOperatorId
        ) || []
      this.targetStoreIds =
        this.coupon.targetStores?.map((store) => store.storeId) || []
      this.targetStoreDeliveryAddressStates =
        this.coupon.targetStoreDeliveryAddressStates?.map(
          (state) => state.state
        ) || []
      this.targetStoreDeliveryAddressZones =
        this.coupon.targetStoreDeliveryAddressZones?.map(
          (zone) => zone.zoneCode
        ) || []
      this.targetType = this.determineTargetType()
    },

    updateCoupon(payload) {
      updateCoupon(this.selectedSupplierId, this.couponId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Coupon has been updated!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          let msg = ''
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'COUPON_OVERLAP':
              msg = 'There is an existing Coupon for the selected dates!'
              break

            default:
              msg = 'Your coupon could not be updated, please try again...'
          }
          this.addNotification({ color: 'error', text: msg })
        })
    },

    saveCoupon() {
      this.saving = true

      const payload = {
        endDate: this.endDate,
        startDate: this.startDate,
        amountOffExGst: Number(this.amountOffExGst),
        minimumOrderValue: Number(this.minimumOrderValue),
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        targetStoreDeliveryAddressStates: this.targetStoreDeliveryAddressStates,
        targetStoreDeliveryAddressZones: this.targetStoreDeliveryAddressZones
      }

      this.newCoupon ? this.createCoupon(payload) : this.updateCoupon(payload)
    },

    // ***
    // Functions related data mapping
    // ***

    mapBannerGroupsList() {
      // Map property names so that it works with V-select component
      this.targetBannerGroups = this.bannerGroups
        .map((bannerGroup) => ({
          text: bannerGroup.displayName,
          value: bannerGroup.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapMultiStoreOperatorsList() {
      // Map property names so that it works with V-select component
      this.targetMultiStoreOperators = this.multiStoreOperators
        .map((mso) => ({
          text: mso.displayName,
          value: mso.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapStoresList() {
      if (this.retailerStores?.length > 0) {
        this.stores = this.retailerStores.sort((a, b) =>
          a.text > b.text ? 1 : -1
        )
      } else {
        this.addNotification({
          color: 'secondary',
          text: `Available ${
            this.isILR ? 'Customer' : 'Retailer'
          } Stores loading, please wait...`
        })
      }
    },

    // ***
    // Helper Functions
    // ***

    determineTargetType() {
      if (this.targetBannerGroupIds.length) return this.BANNER_GROUP
      else if (this.targetMultiStoreOperatorIds.length)
        return this.MULTI_STORE_OPERATOR
      else if (this.targetStoreIds.length) return this.RETAILER_STORE
      else return this.ALL_GEOGRAPHY
    },

    resetTargetTypeForm() {
      this.targetBannerGroupIds = []
      this.targetMultiStoreOperatorIds = []
      this.targetStoreIds = []
    },

    resetTargetGeographyForm() {
      this.targetStoreDeliveryAddressStates = []
      this.targetStoreDeliveryAddressZones = []
    },

    resetRetailers() {
      this.targetStoreIds = []
      this.savedListApplied = false
    },

    handleApplySavedRetailers(ids) {
      this.targetStoreIds = ids
      this.savedListApplied = true
    }
  },

  mounted() {
    this.mapBannerGroupsList()
    this.mapMultiStoreOperatorsList()
    this.mapStoresList()
    this.mapPricingRegionToTradingRule()
    if (!this.newCoupon) this.copyCoupon()
    // Create blueprint of coupon so that we can know if changes were made
    this.originalCoupon = _.cloneDeep(this.createBlueprint())
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#add-coupons {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;
  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
<style lang="scss">
#add-coupons {
  .v-list {
    .v-list-item {
      .v-chip__content {
        font-size: 0.85em;
      }
    }
  }
}
</style>
